import { gql } from "@apollo/client";
import { client } from "../apolloClient";
import { useQuery, useMutation } from "@apollo/client";

/**
 * useProjectMilestoneApi custom hook for project milestone operations.
 * - Provides CRUD operations for project milestones using Apollo Client and GraphQL.
 * - Handles cache updates for milestone queries and mutations.
 * - Exposes helpers for reading/writing/updating milestones in Apollo cache.
 * - Returns methods for fetching, creating, updating, and deleting milestones.
 * - Ensures UI stays in sync with backend by updating Apollo cache after mutations.
 * - Supports milestone status updates and fetching by project.
 */

// GraphQL Queries and Mutations
export const PROJECT_MILESTONE_QUERIES = {
  // Query to fetch all milestones
  GET_PROJECT_MILESTONES: gql`
    query GetProjectMilestone {
      GetProjectMilestone {
        task_id
        project_id
        milestone_code
        parent_task_id
        task_name
        estimated_hours
        task_status_code
        start_date
        end_date
        is_active
        created_by
        created_on
        updated_on
        approved_by
        approved_on
        rejected_by
        rejected_on
        publish_type
        last_published_on
        resource_mappings {
          task_resource_mapping_id
          task_id
          resource_id
          allocation_pc
          resource_budget_hours
          resource_actual_hours
          is_active
          created_on
          updated_on
        }
      }
    }
  `,

  // Query to fetch milestones by project
  GET_PROJECT_MILESTONES_BY_PROJECT_ID: gql`
    query GetProjectMilestonesByProjectId($projectId: Int!) {
      GetProjectMilestonesByProjectId(project_id: $projectId) {
        task_id
        project_id
        milestone_code
        parent_task_id
        task_name
        estimated_hours
        task_status_code
        start_date
        end_date
        is_active
        created_by
        created_on
        updated_on
        approved_by
        approved_on
        rejected_by
        rejected_on
        publish_type
        last_published_on
        resource_mappings {
          task_resource_mapping_id
          task_id
          resource_id
          allocation_pc
          resource_budget_hours
          resource_actual_hours
          is_active
          created_on
          updated_on
        }
      }
    }
  `,

  // Mutation to add a milestone
  ADD_PROJECT_MILESTONE: gql`
    mutation CreateProjectMilestone(
      $project_id: Int!
      $milestone_code: String!
      $milestone_name: String!
      $milestone_description: String!
      $startDate: String
      $endDate: String
    ) {
      CreateProjectMilestone(
        project_id: $project_id
        milestone_code: $milestone_code
        milestone_name: $milestone_name
        milestone_description: $milestone_description
        milestone_start_date: $startDate
        milestone_end_date: $endDate
      ) {
        task_id
        project_id
        milestone_code
        parent_task_id
        task_name
        estimated_hours
        task_status_code
        start_date
        end_date
        is_active
        created_by
        created_on
        updated_on
        approved_by
        approved_on
        rejected_by
        rejected_on
        publish_type
        last_published_on
        resource_mappings {
          task_resource_mapping_id
          task_id
          resource_id
          allocation_pc
          resource_budget_hours
          resource_actual_hours
          is_active
          created_on
          updated_on
        }
      }
    }
  `,

  // Mutation to update a milestone
  UPDATE_PROJECT_MILESTONE: gql`
    mutation UpdateProjectMilestone(
      $milestone_id: ID!
      $project_id: Int
      $milestone_code: String!
      $milestone_name: String
      $milestone_description: String
      $startDate: String
      $endDate: String
      $milestone_status_code: String
    ) {
      UpdateProjectMilestone(
        milestone_id: $milestone_id
        project_id: $project_id
        milestone_code: $milestone_code
        milestone_name: $milestone_name
        milestone_description: $milestone_description
        milestone_start_date: $startDate
        milestone_end_date: $endDate
        milestone_status_code: $milestone_status_code
      ) {
        task_id
        project_id
        milestone_code
        parent_task_id
        task_name
        estimated_hours
        task_status_code
        start_date
        end_date
        is_active
        created_by
        created_on
        updated_on
        approved_by
        approved_on
        rejected_by
        rejected_on
        publish_type
        last_published_on
        resource_mappings {
          task_resource_mapping_id
          task_id
          resource_id
          allocation_pc
          resource_budget_hours
          resource_actual_hours
          is_active
          created_on
          updated_on
        }
      }
    }
  `,

  // Mutation to delete a milestone
  DELETE_PROJECT_MILESTONE: gql`
    mutation DeleteProjectMilestone($milestone_id: ID!) {
      DeleteProjectMilestone(milestone_id: $milestone_id) {
        milestone_id
        project_id
        milestone_code
        milestone_name
        milestone_description
        approved_by
        approved_on
        rejected_by
        rejected_on
        milestone_start_date
        milestone_end_date
        milestone_min_date
        milestone_max_date
        deliverables {
          milestone_id
          milestone_name
          deliverable_id
          deliverable_code
          deliverable_name
          deliverable_status_code
          updated_on
        }
        is_active
        created_on
        updated_on
      }
    }
  `,

  // Mutation to update milestone status
  UPDATE_PROJECT_MILESTONE_STATUS: gql`
    mutation UpdateProjectMilestoneStatus(
      $projectId: ID!
      $publishType: PublishedType!
      $resourceComments: [ResourceComment]
      $type: String
    ) {
      UpdateProjectMilestoneStatus(
        project_id: $projectId
        publish_type: $publishType
        resource_comments: $resourceComments
        type: $type
      ) {
        task_id
        project_id
        milestone_code
        parent_task_id
        task_name
        estimated_hours
        task_status_code
        start_date
        end_date
        is_active
        created_by
        created_on
        updated_on
        approved_by
        approved_on
        rejected_by
        rejected_on
        publish_type
        last_published_on
        resource_mappings {
          task_resource_mapping_id
          task_id
          resource_id
          allocation_pc
          resource_budget_hours
          resource_actual_hours
          is_active
          created_on
          updated_on
        }
      }
    }
  `,
};

// Cache operations for project milestones
const cacheOperations = {
  /**
   * Writes milestones to Apollo Client cache.
   * @param {Array} projectMilestone
   */
  writeProjectMilestones: (projectMilestone) => {
    if (!projectMilestone) return;
    client.writeQuery({
      query: PROJECT_MILESTONE_QUERIES.GET_PROJECT_MILESTONES,
      data: {
        GetProjectMilestone: projectMilestone,
      },
    });
  },

  /**
   * Reads milestones from Apollo Client cache.
   * @returns {Array}
   */
  readProjectMilestones: () => {
    try {
      const { GetProjectMilestone } =
        client.readQuery({
          query: PROJECT_MILESTONE_QUERIES.GET_PROJECT_MILESTONES,
        }) || {};

      return GetProjectMilestone || [];
    } catch {
      return [];
    }
  },

  /**
   * Writes milestones by project ID to Apollo Client cache.
   * @param {number|string} projectId
   * @param {Array} milestones
   */
  writeProjectMilestonesByProjectId: (projectId, milestones) => {
    if (!milestones) return;
    client.writeQuery({
      query: PROJECT_MILESTONE_QUERIES.GET_PROJECT_MILESTONES_BY_PROJECT_ID,
      variables: { projectId },
      data: {
        GetProjectMilestonesByProjectId: milestones,
      },
    });
  },

  /**
   * Reads milestones by project ID from Apollo Client cache.
   * @param {number|string} projectId
   * @returns {Array}
   */
  readProjectMilestonesByProjectId: (projectId) => {
    try {
      const { GetProjectMilestonesByProjectId } =
        client.readQuery({
          query: PROJECT_MILESTONE_QUERIES.GET_PROJECT_MILESTONES_BY_PROJECT_ID,
          variables: { projectId },
        }) || {};

      return GetProjectMilestonesByProjectId || [];
    } catch {
      return [];
    }
  },

  /**
   * Updates a milestone in the Apollo Client cache.
   * @param {Object} updatedProjectMilestone
   */
  updateProjectMilestonesInCache: (updatedProjectMilestone) => {
    const existingProjectMilestones = cacheOperations.readProjectMilestones();
    const updatedProjectMilestones = existingProjectMilestones.map(
      (projectMilestone) =>
        projectMilestone.milestone_id === updatedProjectMilestone.milestone_id
          ? updatedProjectMilestone
          : projectMilestone
    );
    cacheOperations.writeProjectMilestones(updatedProjectMilestones);
  },
};

export const useProjectMilestoneApi = () => {
  const [addProjectMilestoneMutation] = useMutation(
    PROJECT_MILESTONE_QUERIES.ADD_PROJECT_MILESTONE
  );
  const [updateProjectMilestoneMutation] = useMutation(
    PROJECT_MILESTONE_QUERIES.UPDATE_PROJECT_MILESTONE
  );
  const [updateProjectMilestoneStatusMutation] = useMutation(
    PROJECT_MILESTONE_QUERIES.UPDATE_PROJECT_MILESTONE_STATUS
  );

  /**
   * Fetches all project milestones.
   * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
   */
  const getProjectMilestones = async () => {
    try {
      const { data } = await client.query({
        query: PROJECT_MILESTONE_QUERIES.GET_PROJECT_MILESTONES,
        fetchPolicy: "network-only",
      });
      return { success: true, data: data.GetProjectMilestones };
    } catch (error) {
      console.error("Error fetching GetProjectMilestones:", error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Fetches a project milestone by its ID.
   * @param {number|string} milestone_id
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  const getProjectMilestoneById = async (milestone_id) => {
    try {
      const { data } = await client.query({
        query: PROJECT_MILESTONE_QUERIES.GET_PROJECT_MILESTONE_BY_ID,
        variables: { milestone_id },
        fetchPolicy: "network-only",
      });
      return { success: true, data: data.GetProjectResourceById };
    } catch (error) {
      console.error("Error fetching project resource:", error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Creates a new project milestone.
   * @param {Object} formData
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  const createProjectMilestone = async (formData) => {
    try {
      const { data, errors } = await addProjectMilestoneMutation({
        variables: {
          project_id: parseInt(formData.project_id),
          milestone_code: formData.milestone_code,
          milestone_name: formData.milestone_name,
          milestone_description: formData.milestone_description,
          startDate: formData.milestone_start_date,
          endDate: formData.milestone_end_date,
        },
      });

      if (errors?.length > 0) {
        throw new Error(errors[0].message);
      }

      if (!data?.CreateProjectMilestone) {
        throw new Error("Failed to create project milestone");
      }

      return {
        success: true,
        data: data.CreateProjectMilestone,
      };
    } catch (error) {
      // Handle GraphQL errors
      if (error.graphQLErrors?.length > 0) {
        const graphQLError = error.graphQLErrors[0];
        throw new Error(graphQLError.message);
      }

      // Handle network errors
      if (error.networkError?.result?.errors?.length > 0) {
        const networkError = error.networkError.result.errors[0];
        throw new Error(networkError.message);
      }

      throw error;
    }
  };

  /**
   * Updates an existing project milestone.
   * @param {number|string} milestone_id
   * @param {Object} formData
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  const updateProjectMilestone = async (milestone_id, formData) => {
    try {
      const { data, errors } = await updateProjectMilestoneMutation({
        variables: {
          milestone_id: parseInt(milestone_id),
          project_id: parseInt(formData.project_id),
          milestone_code: formData.milestone_code,
          milestone_name: formData.milestone_name,
          milestone_description: formData.milestone_description,
          startDate: formData.milestone_start_date,
          endDate: formData.milestone_end_date,
          milestone_status_code: formData.milestone_status_code
        },
      });

      if (errors?.length > 0) {
        throw new Error(errors[0].message);
      }

      if (!data?.UpdateProjectMilestone) {
        throw new Error("Failed to update ProjectMilestone");
      }

      return {
        success: true,
        data: data.UpdateProjectMilestone,
      };
    } catch (error) {
      // Handle GraphQL errors
      if (error.graphQLErrors?.length > 0) {
        const graphQLError = error.graphQLErrors[0];
        throw new Error(graphQLError.message);
      }

      // Handle network errors
      if (error.networkError?.result?.errors?.length > 0) {
        const networkError = error.networkError.result.errors[0];
        throw new Error(networkError.message);
      }

      throw error;
    }
  };

  /**
   * Deletes a project milestone by its ID.
   * @param {number|string} milestoneId
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  const deleteProjectMilestone = async (milestoneId) => {
    try {
      const { data } = await client.mutate({
        mutation: PROJECT_MILESTONE_QUERIES.DELETE_PROJECT_MILESTONE,
        variables: { milestone_id: parseInt(milestoneId) },
      });

      const existingProjectMilestones = cacheOperations.readProjectMilestones();
      cacheOperations.writeProjectMilestones(
        existingProjectMilestones.filter(
          (projectMilestone) => projectMilestone.milestone_id !== milestoneId
        )
      );

      return { success: true, data: data.DeleteProjectMilestone };
    } catch (error) {
      console.error("Error deleting ProjectMilestone:", error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Fetches milestones by project ID.
   * @param {number|string} projectId
   * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
   */
  const getProjectMilestonesByProjectId = async (projectId) => {
    try {
      const { data } = await client.query({
        query: PROJECT_MILESTONE_QUERIES.GET_PROJECT_MILESTONES_BY_PROJECT_ID,
        variables: { projectId: parseInt(projectId) },
        fetchPolicy: "network-only",
      });
      return { success: true, data: data.GetProjectMilestonesByProjectId };
    } catch (error) {
      console.error("Error fetching project milestones:", error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Updates the status of a project milestone.
   * @param {string|number} projectId
   * @param {string} publishType
   * @param {Array} comments
   * @param {boolean} isSuperAdmin
   * @param {string} type
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  const updateProjectMilestoneStatus = async (
    projectId,
    publishType,
    comments = [],
    isSuperAdmin,
    type = "MANAGER"
  ) => {
    try {
      const { data } = await updateProjectMilestoneStatusMutation({
        variables: {
          projectId: projectId.toString(),
          publishType: publishType,
          resourceComments: comments.map((comment) => ({
            comment: typeof comment === 'string' ? comment : 
                    typeof comment.comment === 'string' ? comment.comment :
                    comment.comment?.comment || ''
          })),
          type: type,
        },
      });

      if (data?.UpdateProjectMilestoneStatus) {
        return { success: true, data: data.UpdateProjectMilestoneStatus };
      } else {
        throw new Error("Failed to update project publish status");
      }
    } catch (error) {
      console.error("Error updating project publish status:", error);
      return { success: false, error: error.message };
    }
  };

  return {
    getProjectMilestones,
    getProjectMilestoneById,
    createProjectMilestone,
    updateProjectMilestone,
    deleteProjectMilestone,
    getProjectMilestonesByProjectId,
    cacheOperations,
    updateProjectMilestoneStatus
  };
};
